/**
 * CustomerDetailsCard Component
 * Displays customer details including avatar, contact info, and notes
 */

import React, { useState } from 'react';
import { Box, Paper, Avatar, TextField, Typography, Button, CircularProgress, Alert } from '@mui/material';
import { LockReset as LockResetIcon } from '@mui/icons-material';
import { getInitials } from '../../../../utils/formatters';
import { usePermissions } from '../../../../hooks/usePermissions';
import { api } from '../../../../services/api.service';
import type { CustomerData, Customer } from '../types';

interface CustomerDetailsCardProps {
  customer: Customer;
  customerData: CustomerData;
  onFieldChange: (field: keyof CustomerData, value: string) => void;
}

export const CustomerDetailsCard: React.FC<CustomerDetailsCardProps> = ({
  customer,
  customerData,
  onFieldChange,
}) => {
  const { isAdmin, canEditAdminFields } = usePermissions();
  const [resetPasswordLoading, setResetPasswordLoading] = useState(false);
  const [resetPasswordSuccess, setResetPasswordSuccess] = useState(false);
  const [resetPasswordError, setResetPasswordError] = useState<string | null>(null);

  const handleResetPassword = async () => {
    if (!customer.id) return;

    setResetPasswordLoading(true);
    setResetPasswordError(null);
    setResetPasswordSuccess(false);

    try {
      await api.post(`/api/v1/admin/customers/${customer.id}/reset-password`);
      setResetPasswordSuccess(true);
      setTimeout(() => setResetPasswordSuccess(false), 5000);
    } catch (error) {
      setResetPasswordError(error instanceof Error ? error.message : 'Failed to send reset email');
    } finally {
      setResetPasswordLoading(false);
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 4,
        mb: 4,
        border: '1px solid #e5e7eb',
        borderRadius: 1.5,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'center', md: 'flex-start' },
          gap: 3,
        }}
      >
        <Avatar
          sx={{
            width: 80,
            height: 80,
            bgcolor: '#111827',
            fontSize: '1.5rem',
            fontWeight: 600,
          }}
        >
          {getInitials(customerData.name || customer.name)}
        </Avatar>
        <Box sx={{ flex: 1, width: { xs: '100%', md: 'auto' } }}>
          {/* Customer Name - Always Editable */}
          <TextField
            fullWidth
            label="Customer Name"
            value={customerData.name}
            onChange={(e) => onFieldChange('name', e.target.value)}
            required
            sx={{
              mb: 1.5,
              '& .MuiOutlinedInput-root': {
                fontSize: '1rem',
                fontWeight: 600,
                color: '#111827',
                backgroundColor: '#f9fafb',
                '& fieldset': {
                  borderColor: '#e5e7eb',
                },
                '&:hover fieldset': {
                  borderColor: '#d1d5db',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#3b82f6',
                  borderWidth: 2,
                },
              },
              '& .MuiInputLabel-root': {
                fontSize: '0.75rem',
                fontWeight: 600,
              },
              '& input': {
                padding: '8px 12px',
              },
            }}
          />

          {/* Email - Always Editable */}
          <TextField
            fullWidth
            label="Email"
            type="email"
            value={customerData.email}
            onChange={(e) => onFieldChange('email', e.target.value)}
            placeholder="No email provided"
            sx={{
              mb: 1.5,
              '& .MuiOutlinedInput-root': {
                fontSize: '0.875rem',
                color: '#6b7280',
                backgroundColor: '#f9fafb',
                '& fieldset': {
                  borderColor: '#e5e7eb',
                },
                '&:hover fieldset': {
                  borderColor: '#d1d5db',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#3b82f6',
                  borderWidth: 2,
                },
              },
              '& .MuiInputLabel-root': {
                fontSize: '0.75rem',
                fontWeight: 600,
              },
              '& input': {
                padding: '8px 12px',
              },
            }}
          />

          {/* Phone - Always Editable */}
          <TextField
            fullWidth
            label="Phone"
            type="tel"
            value={customerData.phone}
            onChange={(e) => onFieldChange('phone', e.target.value)}
            placeholder="(555) 123-4567"
            sx={{
              mb: 1.5,
              '& .MuiOutlinedInput-root': {
                fontSize: '0.875rem',
                color: '#6b7280',
                backgroundColor: '#f9fafb',
                '& fieldset': {
                  borderColor: '#e5e7eb',
                },
                '&:hover fieldset': {
                  borderColor: '#d1d5db',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#3b82f6',
                  borderWidth: 2,
                },
              },
              '& .MuiInputLabel-root': {
                fontSize: '0.75rem',
                fontWeight: 600,
              },
              '& input': {
                padding: '8px 12px',
              },
            }}
          />

          {/* Address - Always Editable */}
          <TextField
            fullWidth
            label="Address"
            value={customerData.address}
            onChange={(e) => onFieldChange('address', e.target.value)}
            placeholder="Street address"
            sx={{
              mb: 1.5,
              '& .MuiOutlinedInput-root': {
                fontSize: '0.875rem',
                color: '#6b7280',
                backgroundColor: '#f9fafb',
                '& fieldset': {
                  borderColor: '#e5e7eb',
                },
                '&:hover fieldset': {
                  borderColor: '#d1d5db',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#3b82f6',
                  borderWidth: 2,
                },
              },
              '& .MuiInputLabel-root': {
                fontSize: '0.75rem',
                fontWeight: 600,
              },
              '& input': {
                padding: '8px 12px',
              },
            }}
          />

          {/* Customer Since - Read Only */}
          <Typography
            variant="body2"
            sx={{
              fontSize: '0.75rem',
              color: '#9ca3af',
            }}
          >
            Customer since {customer.createdAt ? new Date(customer.createdAt).toLocaleDateString() : 'N/A'}
          </Typography>
        </Box>
      </Box>

      {/* Customer Notes - Always Editable */}
      <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #e5e7eb' }}>
        <TextField
          fullWidth
          label="Notes"
          multiline
          rows={2}
          value={customerData.notes}
          onChange={(e) => onFieldChange('notes', e.target.value)}
          placeholder="Add notes about this customer..."
          sx={{
            '& .MuiOutlinedInput-root': {
              fontSize: '0.875rem',
              color: '#1f2937',
              backgroundColor: '#f9fafb',
              '& fieldset': {
                borderColor: '#e5e7eb',
              },
              '&:hover fieldset': {
                borderColor: '#d1d5db',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#3b82f6',
                borderWidth: 2,
              },
            },
            '& .MuiInputLabel-root': {
              fontSize: '0.75rem',
              fontWeight: 600,
            },
            '& textarea': {
              padding: '8px 12px',
            },
          }}
        />
      </Box>

      {/* Admin Notes - Admin Only */}
      {canEditAdminFields && (
        <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #e5e7eb' }}>
          <TextField
            fullWidth
            label="Admin View Only"
            multiline
            rows={2}
            value={customerData.admin_notes || ''}
            onChange={(e) => onFieldChange('admin_notes' as keyof CustomerData, e.target.value)}
            placeholder="Admin-only notes (hidden from customers)..."
            sx={{
              '& .MuiOutlinedInput-root': {
                fontSize: '0.875rem',
                color: '#1f2937',
                backgroundColor: '#fef3c7',
                '& fieldset': {
                  borderColor: '#fbbf24',
                },
                '&:hover fieldset': {
                  borderColor: '#f59e0b',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#d97706',
                  borderWidth: 2,
                },
              },
              '& .MuiInputLabel-root': {
                fontSize: '0.75rem',
                fontWeight: 600,
                color: '#92400e',
              },
              '& textarea': {
                padding: '8px 12px',
              },
            }}
          />
        </Box>
      )}

      {/* Admin Actions - Admin Only */}
      {isAdmin && (
        <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #e5e7eb', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          {resetPasswordSuccess && (
            <Alert severity="success" sx={{ mb: 2, width: '100%' }}>
              Password reset email sent successfully!
            </Alert>
          )}
          {resetPasswordError && (
            <Alert severity="error" sx={{ mb: 2, width: '100%' }}>
              {resetPasswordError}
            </Alert>
          )}
          <Button
            variant="outlined"
            color="primary"
            startIcon={resetPasswordLoading ? <CircularProgress size={16} /> : <LockResetIcon />}
            onClick={handleResetPassword}
            disabled={resetPasswordLoading}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            {resetPasswordLoading ? 'Sending...' : 'Reset Customer Password'}
          </Button>
        </Box>
      )}
    </Paper>
  );
};
